/**
 * Grading maths for the Gradebook.
 *
 * One stored number per mark - a raw score out of its assessment's `max`. A
 * percent is DERIVED from it (`markPercent`, at the bottom of this file) and
 * never stored; Ontario's Growing Success conversion then turns that percent
 * into a level or a letter:
 *
 *   Level 4  80-100  A-, A, A+        Level 2  60-69  C-, C, C+
 *   Level 3  70-79   B-, B, B+        Level 1  50-59  D-, D, D+
 *                                     R        < 50   R
 *
 * Those conversions are for OUTPUT only - the Grade column, the Insights bands,
 * the report card. They are not an input format: a teacher types a score, and
 * `parseMark` in `display.js` accepts nothing else. Levels and letters used to
 * be typeable, and because the parser matched them before it matched a number, a
 * raw `3` on an assessment out of 40 was stored as 73%.
 *
 * What lands on the report card is decided by the report type, NOT the
 * gradebook: Gr 1-6 take a letter grade, Gr 7-12 take a percentage.
 */

export const LEVEL_PERCENT = {
  R: 42,
  "1-": 50, 1: 53, "1+": 57,
  "2-": 60, 2: 63, "2+": 67,
  "3-": 70, 3: 73, "3+": 77,
  "4-": 80, 4: 87, "4+": 95,
};

const LETTER_BANDS = [
  [95, "A+"], [87, "A"], [80, "A-"],
  [77, "B+"], [73, "B"], [70, "B-"],
  [67, "C+"], [63, "C"], [60, "C-"],
  [57, "D+"], [53, "D"], [50, "D-"],
];

const LEVEL_BANDS = [
  [95, "4+"], [87, "4"], [80, "4-"],
  [77, "3+"], [73, "3"], [70, "3-"],
  [67, "2+"], [63, "2"], [60, "2-"],
  [57, "1+"], [53, "1"], [50, "1-"],
];

/* Band from the percent as *displayed*, not as computed. An average of 49.6
 * shows as "50%", so it must read as 1- / D- and not R - otherwise the Grade
 * column's two halves disagree with each other in the same cell. */
function bandLookup(bands, percent) {
  if (percent == null) return null;
  const shown = Math.round(percent);
  for (const [min, label] of bands) if (shown >= min) return label;
  return "R";
}

export const percentToLevel = (p) => bandLookup(LEVEL_BANDS, p);
export const percentToLetter = (p) => bandLookup(LETTER_BANDS, p);

/** Which format this grade's report card actually accepts. */
export function reportFormatFor(grade) {
  const n = Number(grade);
  return n >= 1 && n <= 6 ? "letter" : "percent";
}

/* ── Sheet surfaces ───────────────────────────────────────────────────────
 * Colour carries exactly one meaning: green = a value is here. It is a
 * completion check, not a heatmap - nothing encodes how good the mark is.
 * Everything else is Excel-grey.
 * ──────────────────────────────────────────────────────────────────────── */

/** Semi-transparent so the banding underneath still shows through, but strong
 *  enough to stay obvious against the darkest band. */
export const CELL_ENTERED = "bg-[hsl(var(--brand-500)/0.22)]";

/**
 * Rows AND columns alternate, so every cell sits at the crossing of two cues.
 * Columns carry the heavier tint: a row band is a continuous full-width stripe
 * and reads strongly at any weight, while a column band is chopped into 46px
 * pieces and needs more contrast to register as one line.
 */
export function bandFor(row, col) {
  const r = row % 2 === 1;
  const c = col % 2 === 1;
  if (r && c) return "bg-[hsl(var(--neutral-500)/0.17)]";
  if (c) return "bg-[hsl(var(--neutral-500)/0.12)]";
  if (r) return "bg-[hsl(var(--neutral-500)/0.06)]";
  return "";
}

/* The cursor's row and column glow yellow along their edges, so the student name
 * and the assessment being typed into are both unmistakable. Inset box-shadows
 * rather than borders: they cost no layout, so nothing shifts as focus moves. */
/* Written out in full, not composed from a variable: Tailwind scans source text
 * statically, so a class name built from a template literal never gets compiled. */
export const CROSSHAIR_TINT = "bg-[hsl(45_95%_50%/0.09)]";
export const CROSSHAIR_COL =
  "shadow-[inset_1px_0_0_0_hsl(45_92%_50%),inset_-1px_0_0_0_hsl(45_92%_50%)]";
export const CROSSHAIR_ROW =
  "shadow-[inset_0_1px_0_0_hsl(45_92%_50%),inset_0_-1px_0_0_hsl(45_92%_50%)]";
export const CROSSHAIR_BOTH =
  "shadow-[inset_1px_0_0_0_hsl(45_92%_50%),inset_-1px_0_0_0_hsl(45_92%_50%),inset_0_1px_0_0_hsl(45_92%_50%),inset_0_-1px_0_0_hsl(45_92%_50%)]";
export const GLOW_BORDER = "border-[hsl(45_92%_50%)]";

/**
 * Narrow the sheet to a date range.
 *
 * A report card is a snapshot "as of" a moment, so June marks must not leak into
 * the November report - but the boundary is the teacher's, not ours.
 *
 * This REPLACES a fixed `CYCLES` list of Term 1 / Term 2 / Full year. That list
 * hardcoded 2025-09-01…2026-06-30, so every filter - including "Full year" -
 * returned nothing for a gradebook in any other school year, and the sheet just
 * looked empty. It would have broken every new gradebook from September 2026,
 * when the default school year rolls over.
 *
 * A range is also the only thing that fits reality: Term 1 cutoffs differ by
 * board, and a semestered Gr 9-12 course isn't September-to-June at all.
 *
 * Dates are `YYYY-MM-DD` strings, which compare correctly with `<=` - no Date
 * objects, and so no timezone to get wrong. An empty bound means "unbounded",
 * so a teacher can clear one side and see everything up to or from a date.
 */
export function assessmentsInRange(assessments, from, to) {
  return assessments.filter(
    (a) => (!from || a.date >= from) && (!to || a.date <= to),
  );
}

/**
 * The range a sheet should OPEN on: the span of the assessments that exist, so
 * every column is visible until the teacher narrows it by hand.
 *
 * Deliberately not the gradebook's school year. That was the default once, and
 * it hid real data - a book labelled 2025-2026 holding September and October
 * 2026 assessments opened completely empty, because both fell outside
 * Sep 2025 - Jun 2026. The dates were sensible; the label simply disagreed with
 * them, and the filter sided with the label.
 *
 * The school year is only a fallback for a gradebook with nothing in it yet,
 * where there is no span to measure.
 */
export function defaultRange(assessments, schoolYear, schoolYearRange) {
  const dates = assessments.map((a) => a.date).sort();
  return dates.length
    ? { from: dates[0], to: dates[dates.length - 1] }
    : schoolYearRange(schoolYear);
}

/**
 * What a diagonal header shows when the sheet is condensed. There is no short
 * code to maintain: the assessment has one name, and the condensed view simply
 * clips it. The full name (with its category and weight) is one hover away, and
 * the expanded view shows it wrapped in full.
 */
export function truncateLabel(title, max = 22) {
  const s = String(title ?? "").trim();
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}

/* ── Marks ───────────────────────────────────────────────────────────────── */

/**
 * Ontario's four achievement-chart categories - Knowledge and understanding,
 * Thinking, Communication, Application - and the ONE order they are ever listed
 * in, because it is the order the chart itself uses. A teacher reading a report
 * card and a teacher reading this sheet must see the same sequence.
 *
 * Growing Success makes the chart mandatory as a FRAMEWORK but asks only for
 * balance across the four, not fixed percentages, so a split is opt-in per
 * assessment and never imposed on a column.
 *
 * A split assessment carries a max per section (`maxK`...`maxA`); an entry
 * carries a score per section (`scoreK`...`scoreA`). Both are nullable, and on
 * the MAX that null is load-bearing: a null max means the section WAS NOT
 * ASSESSED - not that it scored zero. There is no separate "assessed?" flag to
 * drift out of step with the number, and no zero-max case, because a section
 * with no denominator contributes nothing to `sum(scores) / sum(maxes)` - which
 * is exactly what "not assessed" should do. A zero would be a different claim,
 * and a wrong one: that the student earned nothing on something they were tested
 * on, dragging the total down.
 */
export const SECTIONS = ["K", "T", "C", "A"];
export const SECTION_MAX_FIELD = { K: "maxK", T: "maxT", C: "maxC", A: "maxA" };
export const SECTION_SCORE_FIELD = { K: "scoreK", T: "scoreT", C: "scoreC", A: "scoreA" };

/** The achievement chart's own wording. Anywhere a section is named in full -
 *  the create modal, a header hover card - it is named from here, so the sheet
 *  and the report card cannot end up calling the same category two things. */
export const SECTION_NAME = {
  K: "Knowledge and Understanding",
  T: "Thinking",
  C: "Communication",
  A: "Application",
};

/**
 * The sections this assessment assesses, in K/T/C/A order. EMPTY means an
 * ordinary single-mark assessment.
 *
 * That emptiness IS the single-vs-split test everywhere; there is deliberately
 * no `isSplit` column, because the maxes already answer the question and a
 * second copy of the answer could disagree with them. The shape is immutable
 * server-side (a single can never become a split, nor the reverse - both would
 * have to invent or discard marks), so this never changes under a stored mark.
 */
export function sectionsOf(assessment) {
  if (!assessment) return [];
  return SECTIONS.filter((s) => assessment[SECTION_MAX_FIELD[s]] != null);
}

/** How many of this assessment's sections actually carry a score. */
export function marked(mark, assessment) {
  if (!mark) return 0;
  return sectionsOf(assessment).filter((s) => mark[SECTION_SCORE_FIELD[s]] != null).length;
}

/**
 * The split's total: the sum of the section scores that are there.
 *
 * Driven by `sectionsOf`, not by the mark's own keys, so a stray `scoreC` left
 * on an entry whose `maxC` is null cannot leak into a total it has no
 * denominator in. An unmarked section adds nothing, which makes this a running
 * subtotal rather than a mark until `marked() === sectionsOf().length` - see
 * `markPercent` for why that distinction is the whole point.
 */
export function rolledUp(mark, assessment) {
  return sectionsOf(assessment).reduce(
    (sum, s) => sum + (Number(mark?.[SECTION_SCORE_FIELD[s]]) || 0),
    0,
  );
}

/**
 * Percent for a mark, or null when it shouldn't count.
 *
 * THE one place a percent comes from. A mark stores raw scores and nothing else;
 * the percent is computed here, on every render, from those and the assessment's
 * `max`. It is never stored - `entries.percent` was dropped (migration
 * `drop_entry_percent`) precisely so there can be no second copy of this number
 * to drift out of step with the marks.
 *
 * Excused is NOT a zero; it drops out of the average. Missing IS a zero. There
 * used to be an `absent` beside excused, and it did arithmetically the identical
 * thing - drop out - so the sheet carried two words for one behaviour and a
 * teacher had to guess which to reach for. Excused survived because it covers
 * both readings; absent never covered an IEP exemption.
 *
 * The split branch is the only genuinely new rule: a PART-marked assessment
 * returns null and so drops out of the weighted average, exactly as excused
 * does. Part-marked is the normal working state, not an error - you mark section
 * K across thirty papers, then section T - so for the days that takes, reading
 * the blank sections as zeroes would drag every running grade in the class down
 * to a number nobody's work justifies. A grade built only from evidence that is
 * complete is right; one built from blanks is wrong for as long as marking runs.
 *
 * It divides by `max`, not by the sum of the section maxes, and the two are the
 * same number by construction: the backend rejects a split whose sections do not
 * sum to `max` (service 400, plus the CHECK `assessments_split_max_is_sum` as a
 * backstop). `max` is also what the column header shows, so the arithmetic on
 * screen matches the arithmetic here.
 */
export function markPercent(mark, assessment) {
  if (!mark) return null;
  if (mark.status === "excused") return null;
  if (mark.status === "missing") return 0;

  const sections = sectionsOf(assessment);
  if (sections.length) {
    if (marked(mark, assessment) < sections.length) return null;
    return assessment.max ? (rolledUp(mark, assessment) / assessment.max) * 100 : null;
  }

  if (mark.score != null && assessment?.max) return (mark.score / assessment.max) * 100;
  return null;
}

export function weightedAverage(assessments, marksByAssessment) {
  let num = 0;
  let den = 0;
  for (const a of assessments) {
    const p = markPercent(marksByAssessment[a.id], a);
    if (p == null) continue;
    num += p * a.weight;
    den += a.weight;
  }
  return den === 0 ? null : num / den;
}

/** "2025 Sep 12" - year first, so a column of dates sorts by eye. */
export function formatDateLong(iso) {
  const [y, m, d] = String(iso).split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${y} ${months[Number(m) - 1]} ${Number(d)}`;
}

export function formatDate(iso) {
  const [, m, d] = iso.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m) - 1]} ${Number(d)}`;
}
